import { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';
import CurrencySymbol from './CurrencySymbol';

export default function POViewer({ po, onClose }) {
  const [loading, setLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState('');

  const poCurrency = po.currency || 'USD';

  useEffect(() => {
    // Fetch company logo from public endpoint
    const fetchCompanySettings = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/public/settings/company`);
        if (response.ok) {
          const data = await response.json();
          if (data.company_logo) {
            setCompanyLogo(data.company_logo);
          }
        }
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
      }
    };
    fetchCompanySettings();
  }, []);

  const downloadPDF = async () => {
    setLoading(true);
    console.log('[PDF] Starting PO PDF download...');
    
    try {
      const element = document.getElementById('po-content');
      if (!element) {
        throw new Error('PO content not found');
      }

      // Store original styles
      const originalPadding = element.style.padding;
      const originalMaxWidth = element.style.maxWidth;
      const originalMargin = element.style.margin;
      const originalWidth = element.style.width;
      
      // Temporarily adjust for PDF capture - full width, minimal padding
      element.style.padding = '10px';
      element.style.maxWidth = 'none';
      element.style.margin = '0';
      element.style.width = '210mm'; // A4 width

      // Wait for style changes and images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(element, {
        scale: 2.5,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 0,
        windowWidth: 794, // A4 width in pixels at 96 DPI
        width: element.offsetWidth
      });

      // Restore original styles
      element.style.padding = originalPadding;
      element.style.maxWidth = originalMaxWidth;
      element.style.margin = originalMargin;
      element.style.width = originalWidth;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Minimal margins
      const margin = 5;
      const contentWidth = pdfWidth - (2 * margin);
      const contentHeight = pdfHeight - (2 * margin);
      
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let pageCount = 0;

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      // Add pages with minimal margins
      while (heightLeft > 0 || pageCount === 0) {
        if (pageCount > 0) {
          pdf.addPage();
        }
        
        const yPosition = pageCount === 0 ? margin : margin - (pageCount * contentHeight);
        pdf.addImage(imgData, 'JPEG', margin, yPosition, imgWidth, imgHeight);
        
        heightLeft -= contentHeight;
        pageCount++;
        
        if (heightLeft <= 0) break;
      }

      // Generate blob
      let blob = pdf.output('blob');
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Generated PDF blob is empty');
      }

      // Ensure correct MIME type
      if (blob.type !== 'application/pdf') {
        blob = new Blob([await blob.arrayBuffer()], { type: 'application/pdf' });
      }

      // Create object URL
      const url = URL.createObjectURL(blob);

      // Create download link with proper attributes
      const filename = `${po.po_number}.pdf`;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);

      // Trigger download with MouseEvent (more reliable)
      const event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      link.dispatchEvent(event);

      // Cleanup after 1.5 seconds
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn('Failed to revoke object URL:', e);
        }
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      }, 1500);

      console.log(`[PDF] Download triggered: ${filename} (${blob.size} bytes)`);
      
    } catch (error) {
      console.error('[PDF] Download failed:', error);
      alert(`Failed to generate PDF: ${error.message}\n\nPlease check console for details.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header Controls */}
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold">{po.po_number}</h2>
          <div className="flex gap-2">
            <Button 
              type="button"
              onClick={downloadPDF} 
              disabled={loading} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Download className="w-4 h-4 mr-2" />
              {loading ? 'Generating...' : 'Download PDF'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* PO Content */}
        <div id="po-content" className="p-8 bg-white max-w-[210mm] mx-auto">
          {/* Header with Logo */}
          <div className="flex justify-between items-start mb-8">
            <div className="flex items-start gap-4">
              {companyLogo && (
                <img 
                  src={companyLogo} 
                  alt="Company Logo" 
                  className="h-16 w-auto object-contain"
                />
              )}
              <div>
                <h1 className="text-2xl font-bold text-slate-900">PURCHASE ORDER</h1>
                <p className="text-lg mt-2 text-slate-600">{po.po_number}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">Date: {new Date(po.date).toLocaleDateString()}</p>
              <p className="text-sm text-slate-600 mt-1">Currency: <span className="font-bold">{poCurrency}</span></p>
            </div>
          </div>

          {/* Supplier Info */}
          <div className="mb-8 p-4 bg-slate-50 rounded">
            <h3 className="font-bold text-slate-900 mb-2">Supplier:</h3>
            <p className="text-slate-700 text-lg">{po.supplier_name}</p>
          </div>

          {/* Items Table */}
          <table className="w-full mb-8">
            <thead className="bg-slate-100 border-b-2 border-slate-300">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">#</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Item</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Quantity</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Unit Price</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {po.items.map((item, index) => (
                <tr key={index} className="border-b">
                  <td className="px-4 py-3">{index + 1}</td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-semibold">{item.item_name || item.product_name}</p>
                      {item.description && <p className="text-xs text-slate-500">{item.description}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">{item.quantity} {item.unit || 'pcs'}</td>
                  <td className="px-4 py-3 text-right">
                    <CurrencySymbol currency={poCurrency} />
                    {item.price.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">
                    <CurrencySymbol currency={poCurrency} />
                    {item.total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-80 space-y-2">
              <div className="flex justify-between text-slate-700">
                <span>Subtotal ({poCurrency}):</span>
                <span className="font-semibold">
                  <CurrencySymbol currency={poCurrency} />
                  {po.subtotal.toFixed(2)}
                </span>
              </div>
              {po.tax > 0 && (
                <div className="flex justify-between text-slate-700">
                  <span>Tax ({po.tax}%):</span>
                  <span className="font-semibold">
                    <CurrencySymbol currency={poCurrency} />
                    {((po.total - po.subtotal)).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t-2 border-slate-300">
                <span>Total ({poCurrency}):</span>
                <span>
                  <CurrencySymbol currency={poCurrency} />
                  {po.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Category */}
          <div className="mb-6">
            <p className="text-sm text-slate-600">
              <span className="font-semibold">Category:</span> {po.category_name}
            </p>
          </div>

          {/* Notes */}
          {po.notes && (
            <div className="mb-6 p-4 bg-slate-50 rounded">
              <h3 className="font-bold text-slate-900 mb-2">Notes:</h3>
              <p className="text-slate-700 whitespace-pre-wrap">{po.notes}</p>
            </div>
          )}

          {/* Status */}
          <div className="text-center pt-8 border-t">
            <p className="text-sm text-slate-500">
              Status: <span className={`font-bold ${
                po.status === 'completed' ? 'text-green-600' :
                po.status === 'pending' ? 'text-yellow-600' :
                'text-slate-600'
              }`}>
                {po.status.toUpperCase()}
              </span>
            </p>
            {po.pushed_to_expense && (
              <p className="text-sm text-emerald-600 mt-1">
                ✓ Recorded as Expense: {po.expense_id}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
